import { useMemo, useState, useEffect } from "react"
import { Box, Button, Flex, Heading, Text, Badge, Menu, Link } from "@chakra-ui/react"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, ReferenceLine, Tooltip, XAxis, YAxis, ResponsiveContainer, Line, ReferenceArea, Legend } from "recharts"
import type { Graph, Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"
import type { DateTime } from "timezonecomplete"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import {
  appendTimeMs,
  chartHighlightToMsRange,
  TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX,
  timescaleChartMarginRight,
  timescaleMinTickGap,
  timescaleSpanMs,
  timescaleTickFormatter,
  timescaleTimeDomain,
  niceYAxisMax,
  type TimeSeriesDataPoint,
  type TimeSeriesSeries,
  type AssetConfig,
  type ChartHighlightRange,
} from "./TimeSeries"
import { PEAK_COLOR, OVERLAY_LINE_COLOR } from "./graphColors"
import PhaseBalanceChart, { type PhaseDataPoint, type PhaseChartMode } from "./PhaseBalanceChart"

const TIMESCALE_MONITORING_CHART_HEIGHT = "400px"

interface StackedBarBreakdownChartProps {
  data: TimeSeriesDataPoint[]
  series: TimeSeriesSeries<string[]>[]
  aggregatedData?: TimeSeriesDataPoint[]
  graph: Graph
  showBackToggle: boolean
  onBack: () => void
  assetConfigs?: AssetConfig[]
  overlayData?: {
    data: TimeSeriesDataPoint[]
    series: TimeSeriesSeries<string[]>[]
    yAxisLabel: string
    /** When true, overlay line shares the left Y-axis with bars (same kW scale). */
    useSameYAxis?: boolean
  }
  showPeakToggle?: boolean
  /** Initial "Show peaks" state for stacked bars (default true) */
  defaultShowPeaks?: boolean
  /**
   * Marks this as the Load graph (formerly used to label the filter menu
   * "Consumers"). The menu now always says "Assets" regardless, since a load
   * source such as an inverter or DC-AC solar is an asset, not a consumer.
   * Retained for API compatibility; no longer changes the menu wording.
   */
  useConsumerMenu?: boolean
  /** Draw the phase chart per ticked asset instead of one combined stack. */
  phasePerAsset?: boolean
  /** Chart plot height (matches line charts on Timescale monitoring) */
  chartHeight?: string
  /** Shown next to title; same interval tooltip as shared TimeSeries graphs */
  timeseries?: Timeseries
  /** Time ranges to highlight on the chart (e.g. alert event periods) */
  highlightRanges?: ChartHighlightRange[]
  /** Shared timestamps across all graphs for x-axis alignment */
  sharedTimestamps?: string[]
  /** When set, matches other Timescale monitoring charts' X tick spacing */
  xAxisTickInterval?: number
  /** Used with dynamic interval when xAxisTickInterval is omitted (e.g. alert popover). */
  chartAxisStartDate?: DateTime
  chartAxisEndDate?: DateTime
  /** Shared with other monitoring charts so tick times match (e.g. battery vs power). */
  xAxisTicksMs?: number[]
  /** Raw per-consumer per-phase power data for the Phase Balance Chart */
  phaseBalanceData?: PhaseDataPoint[]
  /** When true, render the Phase Balance Chart and SharedLegend below the main chart */
  phaseDataAvailable?: boolean
  /**
   * Display mode for the phase balance chart.
   * `"absolute"` (default) shows a standard stacked bar with the same kW scale
   * as the site-load chart, making per-phase spikes clearly visible.
   * `"percentage"` shows the legacy 100 % stacked view.
   */
  phaseChartMode?: PhaseChartMode
  /** When set, the specified phase is visually emphasised and others are dimmed in the phase chart */
  highlightPhase?: 1 | 2 | 3
}

const REMAINDER_COLOR = OVERLAY_LINE_COLOR
const REMAINDER_KEY = "_remainder"

const OTHER_SERIES_STUB: TimeSeriesSeries<string[]> = {
  name: REMAINDER_KEY,
  color: REMAINDER_COLOR,
  label: "Other",
  strokeWidth: 0,
  connectNulls: false,
}
const MIN_BAR_HEIGHT_PX = 3

const formatValueWithDecimals = (value: number): string => {
  if (value === 0 || Number.isInteger(value)) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }
  if (Math.abs(value) < 1) {
    return value.toFixed(4)
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

interface CustomBarTooltipProps {
  active?: boolean
  payload?: any[]
  label?: string
  series: TimeSeriesSeries<string[]>[]
  aggregatedData?: TimeSeriesDataPoint[]
  unit?: string
  data?: TimeSeriesDataPoint[]
  /** Shown next to the system total (e.g. "System load") */
  totalLabel?: string
  /** When false, hide per-segment peak lines in the tooltip (matches bar peak caps) */
  showPeaks?: boolean
}

function resolveDataPointForTooltipLabel(
  label: string | number | undefined,
  data: TimeSeriesDataPoint[] | undefined
): TimeSeriesDataPoint | undefined {
  if (label === undefined || !data?.length) return undefined
  if (typeof label === "number") {
    return data.find((d) => (d as { timeMs?: number }).timeMs === label)
  }
  return data.find((d) => d.timestamp === label)
}

interface StackedBarLegendProps {
  payload?: readonly any[]
  onSeriesHover: (seriesName: string | null) => void
  hoveredSeries: string | null
  seriesLabelMap: Map<string, string>
}

const StackedBarLegend = ({ payload, onSeriesHover: _onSeriesHover, hoveredSeries: _hoveredSeries, seriesLabelMap }: StackedBarLegendProps) => {
  if (!payload || !payload.length) return null

  // Sort so legend reads top-to-bottom matching the stack (topmost bar first).
  // "Other" is the topmost bar so it appears first.
  const sorted = (() => {
    const others: any[] = []
    const rest: any[] = []
    for (const entry of payload) {
      const key = String(entry.value || entry.dataKey || "")
      if (key === REMAINDER_KEY) {
        others.push(entry)
      } else {
        rest.push(entry)
      }
    }
    return [...others, ...rest.reverse()]
  })()

  return (
    <Flex direction="row" gap={4} justifyContent="center" alignItems="center" mb={2} flexWrap="wrap">
      {sorted.map((entry, index) => {
        const seriesKey = entry.value || entry.dataKey
        const displayLabel = seriesLabelMap.get(seriesKey) || entry.name || seriesKey
        const isLine = String(seriesKey).startsWith("overlay_") || seriesKey === NET_LOAD_KEY

        return (
          <Flex
            key={`legend-${index}`}
            data-legend-item
            alignItems="center"
            gap={1}
            fontSize="sm"
          >
            <Box
              width="12px"
              height={isLine ? "2px" : "12px"}
              backgroundColor={entry.color}
              borderRadius={isLine ? "0" : "sm"}
            />
            <Text color="gray.700">{displayLabel}</Text>
          </Flex>
        )
      })}
    </Flex>
  )
}

const CustomBarTooltip = ({
  active,
  payload,
  label,
  series,
  aggregatedData,
  unit,
  data,
  totalLabel = "Total",
  showPeaks = true,
}: CustomBarTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  // Split payload into bar series and overlay series
  const barPayload: any[] = []
  const overlayPayload: any[] = []
  
  payload.forEach((item) => {
    const itemName = String(item.name || item.dataKey || "")
    // The net load line is already reported as the tooltip's total row, so keep
    // it out of the per-segment breakdown to avoid double-counting it.
    if (itemName === NET_LOAD_KEY) return
    if (itemName.startsWith("overlay_")) {
      overlayPayload.push(item)
    } else {
      barPayload.push(item)
    }
  })

  const aggregatedMap = new Map(
    aggregatedData?.map((p) => [p.timestamp, p.value]) || []
  )
  const rowForLabel = resolveDataPointForTooltipLabel(label, data)
  const aggregatedTotal = rowForLabel
    ? aggregatedMap.get(rowForLabel.timestamp as string)
    : typeof label === "string"
      ? aggregatedMap.get(label)
      : undefined
  const total =
    typeof aggregatedTotal === "number" && Number.isFinite(aggregatedTotal)
      ? aggregatedTotal
      : barPayload.reduce((sum, item) => sum + (item.value || 0), 0)

  // Build a legend-order index so tooltip items match the stack top-to-bottom
  // "Other" (_remainder) should always appear last
  const legendOrder = new Map<string, number>()
  series.forEach((s, i) => legendOrder.set(s.name, i))

  const validBarPayload = barPayload
    .filter((item) => {
      const value = typeof item.value === "number" ? item.value : parseFloat(String(item.value))
      return !isNaN(value) && item.value !== null && item.value !== undefined && item.value !== 0
    })
    .sort((a, b) => {
      const aIsOther = a.name === REMAINDER_KEY ? 1 : 0
      const bIsOther = b.name === REMAINDER_KEY ? 1 : 0
      // Other always first (topmost bar in the stack)
      if (aIsOther !== bIsOther) return bIsOther - aIsOther
      // Reverse legend order so topmost bar (highest index) appears first
      const aIdx = legendOrder.get(a.name) ?? Number.MAX_SAFE_INTEGER
      const bIdx = legendOrder.get(b.name) ?? Number.MAX_SAFE_INTEGER
      return bIdx - aIdx
    })

  if (validBarPayload.length === 0 && overlayPayload.length === 0) {
    return null
  }

  const labelMs = typeof label === "number" ? label : new Date(String(label || "")).getTime()
  const date = new Date(Number.isFinite(labelMs) ? labelMs : String(label || ""))
  const formattedDate = date.toLocaleString([], {
    timeZone: IANATimeZone(),
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const dataPoint = resolveDataPointForTooltipLabel(label, data)

  return (
    <Box
      backgroundColor="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      padding={3}
      boxShadow="lg"
      minW="250px"
      zIndex="tooltip"
      position="relative"
    >
      <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>
        {formattedDate}
      </Text>

      {validBarPayload.length > 0 && (
        <>
          {/* Total peak value across all assets */}
          {(() => {
            const totalPeak = validBarPayload.reduce((sum, entry) => {
              const peakKey = `${entry.name}_peak`
              const peakValue = dataPoint?.[peakKey]
              if (typeof peakValue === "number" && Number.isFinite(peakValue)) {
                return sum + peakValue
              }
              return sum
            }, 0)
            if (showPeaks && totalPeak > 0 && totalPeak > total) {
              return (
                <Flex mb={2} pb={2} borderBottomWidth="1px" borderColor="gray.200" direction="column" gap={1}>
                  <Flex>
                    <Text fontSize="sm" fontWeight="bold" flex="1">
                      {totalLabel}
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {formatValueWithDecimals(total)}{unit ? ` ${unit}` : ""}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" gap={2}>
                    <Badge
                      backgroundColor={PEAK_COLOR}
                      color="white"
                      fontSize="xs"
                      minW="12px"
                      textAlign="center"
                    >
                      ▲
                    </Badge>
                    <Text fontSize="xs" color="gray.600" flex="1">
                      {unit && /W$/.test(unit) ? "Peak power" : "Peak"}
                    </Text>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                      {formatValueWithDecimals(totalPeak)}{unit ? ` ${unit}` : ""}
                    </Text>
                  </Flex>
                </Flex>
              )
            }
            return (
              <Flex mb={2} pb={2} borderBottomWidth="1px" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="bold" flex="1">
                  {totalLabel}
                </Text>
                <Text fontSize="sm" fontWeight="bold">
                  {formatValueWithDecimals(total)}{unit ? ` ${unit}` : ""}
                </Text>
              </Flex>
            )
          })()}

          <Flex direction="column" gap={1}>
            {validBarPayload.map((entry, index) => {
              const seriesConfig = series.find((s) => s.name === entry.name)
              const displayLabel = seriesConfig?.label || String(entry.name)
              const numericValue =
                typeof entry.value === "number" ? entry.value : parseFloat(String(entry.value))
              const formattedValue = !isNaN(numericValue) ? formatValueWithDecimals(numericValue) : "n/a"
              const percentage = total > 0 ? ((numericValue / total) * 100).toFixed(1) : "0.0"

              return (
                <Flex key={entry.name || index} direction="column" gap={1}>
                  <Flex alignItems="center" gap={2}>
                    <Badge
                      backgroundColor={entry.color || "#999"}
                      color="white"
                      fontSize="xs"
                      minW="12px"
                      textAlign="center"
                    >
                      ●
                    </Badge>
                    <Text fontSize="sm" color="gray.700" flex="1">
                      {displayLabel}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {percentage}%
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                      {formattedValue}{unit ? ` ${unit}` : ""}
                    </Text>
                  </Flex>
                </Flex>
              )
            })}
          </Flex>
        </>
      )}

      {overlayPayload.length > 0 && (
        <Flex direction="column" gap={1} mt={validBarPayload.length > 0 ? 2 : 0} pt={validBarPayload.length > 0 ? 2 : 0} borderTopWidth={validBarPayload.length > 0 ? "1px" : "0"} borderColor="gray.200">
          {overlayPayload.map((entry, index) => {
            const itemName = String(entry.name || entry.dataKey || "")
            const seriesConfig = series.find((s) => s.name === itemName)
            const displayLabel = seriesConfig?.label || "Site load"
            const numericValue =
              typeof entry.value === "number" ? entry.value : parseFloat(String(entry.value))
            const formattedValue = !isNaN(numericValue) && numericValue !== null ? formatValueWithDecimals(numericValue) : "n/a"

            return (
              <Flex key={itemName || index} alignItems="center" gap={2}>
                <Badge
                  backgroundColor={entry.color || "#999"}
                  color="white"
                  fontSize="xs"
                  minW="12px"
                  textAlign="center"
                >
                  ●
                </Badge>
                <Text fontSize="sm" color="gray.700" flex="1">
                  {displayLabel}
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                  {formattedValue}{unit ? ` ${unit}` : ""}
                </Text>
              </Flex>
            )
          })}
        </Flex>
      )}
    </Box>
  )
}

const PEAK_OPACITY = 0.6

/**
 * Net load line: the signed sum of the visible breakdown series, drawn on the
 * same axis as the bars so the true site load reads directly off the chart when
 * some entities are feeding power back (negative values). Uses the shared
 * overlay-line colour, which is reserved for exactly this kind of reference line.
 */
const NET_LOAD_KEY = "_netLoad"
const NET_LOAD_LABEL = "System Load"

interface CustomBarShapeProps {
  fill: string
  x: number
  y: number
  width: number
  height: number
  payload?: any
  dataKey?: string
  data?: TimeSeriesDataPoint[]
  yScale?: any
  showPeaks?: boolean
  /** When true, this is the topmost bar in the stack and should render the total peak cap */
  isTopOfStack?: boolean
  /** All bar series names (excluding overlays) for computing total peak on the topmost bar */
  allBarSeriesNames?: string[]
}

const CustomBarShape = (props: CustomBarShapeProps) => {
  const { fill, x, y, width, height: rawHeight, payload, dataKey, data, showPeaks = true, isTopOfStack, allBarSeriesNames } = props

  if (!Number.isFinite(rawHeight)) {
    return <rect x={x} y={y} width={width} height={0} fill="transparent" />
  }

  // Recharts passes a negative height for a segment whose value is negative
  // (e.g. an inverter/charger feeding back — a "-ve load entity"). With
  // stackOffset="sign" these are positioned below the zero line; normalise the
  // height/y so we render an opaque downward rect there.
  const isNegativeSegment = rawHeight < 0
  const height = Math.abs(rawHeight)
  const rectY = isNegativeSegment ? y + rawHeight : y

  const baseBar = height > 0 && height < MIN_BAR_HEIGHT_PX ? (
    <g>
      <rect x={x} y={rectY} width={width} height={height} fill="transparent" />
      <rect
        x={x}
        y={isNegativeSegment ? rectY : rectY - MIN_BAR_HEIGHT_PX + height}
        width={width}
        height={MIN_BAR_HEIGHT_PX}
        fill={fill}
        opacity={0.8}
      />
    </g>
  ) : (
    <rect x={x} y={rectY} width={width} height={height} fill={fill} />
  )

  // Negative segments are drawn opaque below zero and carry no peak cap.
  if (isNegativeSegment) {
    return baseBar
  }

  if (!showPeaks || !payload || !dataKey || !data) {
    return baseBar
  }

  // For the topmost bar in the stack, render a total peak cap
  if (isTopOfStack && allBarSeriesNames) {
    // Sum all bar values and all peak values for this data point
    let totalValue = 0
    let totalPeak = 0
    let hasPeakData = false

    for (const seriesName of allBarSeriesNames) {
      const val = payload[seriesName]
      if (typeof val === "number" && Number.isFinite(val) && val > 0) {
        totalValue += val
      }
      const peakKey = `${seriesName}_peak`
      const peakVal = payload[peakKey]
      if (typeof peakVal === "number" && Number.isFinite(peakVal)) {
        totalPeak += peakVal
        hasPeakData = true
      }
    }

    // Also include _remainder value in total
    const remainderVal = payload[REMAINDER_KEY]
    if (typeof remainderVal === "number" && Number.isFinite(remainderVal) && remainderVal > 0) {
      totalValue += remainderVal
    }

    if (!hasPeakData || totalPeak <= totalValue || totalValue <= 0 || height <= 0) {
      return baseBar
    }

    // Calculate the peak extension above the total stack
    const peakExtension = totalPeak - totalValue

    // Use the ratio of peak extension to the current bar's value
    // to estimate pixel height, since the topmost bar's height maps to its value
    const currentBarValue = typeof payload[dataKey] === "number" ? payload[dataKey] : 0
    const simpleRatio = currentBarValue > 0 ? (peakExtension / currentBarValue) : 0
    const estimatedPeakPx = simpleRatio * height

    const minCapHeight = 3
    const finalPeakHeight = Math.max(estimatedPeakPx, minCapHeight)
    const peakY = y - finalPeakHeight

    return (
      <g>
        {baseBar}
        <rect
          x={x}
          y={peakY}
          width={width}
          height={finalPeakHeight}
          fill={PEAK_COLOR}
          opacity={PEAK_OPACITY}
        />
      </g>
    )
  }

  // For non-topmost bars, only render individual peak if there's no remainder in the stack
  const hasRemainder = typeof payload[REMAINDER_KEY] === "number" && payload[REMAINDER_KEY] > 0
  if (hasRemainder) {
    return baseBar
  }

  const peakKey = `${dataKey}_peak`
  const avgValue = payload[dataKey]
  const peakValue = payload[peakKey]

  if (
    typeof avgValue !== "number" ||
    typeof peakValue !== "number" ||
    !Number.isFinite(avgValue) ||
    !Number.isFinite(peakValue) ||
    peakValue < avgValue ||
    avgValue <= 0 ||
    height <= 0
  ) {
    return baseBar
  }

  const peakExtension = peakValue - avgValue

  // Height above the bar segment; can be zero when peak equals average — still show min cap below
  const peakHeightPx =
    avgValue > 0 && peakExtension > 0 ? (peakExtension / avgValue) * height : 0

  const minCapHeight = 3
  const finalPeakHeight = Math.max(peakHeightPx, minCapHeight)
  const peakY = y - finalPeakHeight

  return (
    <g>
      {baseBar}
      <rect
        x={x}
        y={peakY}
        width={width}
        height={finalPeakHeight}
        fill={PEAK_COLOR}
        opacity={PEAK_OPACITY}
      />
    </g>
  )
}

const StackedBarBreakdownChart = ({
  data,
  series,
  aggregatedData,
  graph,
  showBackToggle,
  onBack,
  assetConfigs: _assetConfigs,
  overlayData,
  showPeakToggle = false,
  defaultShowPeaks = true,
  useConsumerMenu: _useConsumerMenu = false,
  phasePerAsset = false,
  chartHeight = TIMESCALE_MONITORING_CHART_HEIGHT,
  timeseries,
  highlightRanges,
  sharedTimestamps,
  xAxisTickInterval,
  chartAxisStartDate,
  chartAxisEndDate,
  xAxisTicksMs,
  phaseBalanceData,
  phaseDataAvailable,
  phaseChartMode = "absolute",
  highlightPhase,
}: StackedBarBreakdownChartProps) => {
  /** When aggregated system load exists but per-asset breakdown is empty, still render grey remainder-only bars. */
  const breakdownInputData = useMemo((): TimeSeriesDataPoint[] => {
    let baseData = data.length > 0 ? data : []
    if (baseData.length === 0 && aggregatedData?.length) {
      baseData = aggregatedData.map((p) => ({ timestamp: p.timestamp as string }))
    }
    
    // Backfill with shared timestamps if available
    if (sharedTimestamps && sharedTimestamps.length > 0) {
      const dataMap = new Map(baseData.map((point) => [point.timestamp as string, point]))
      return sharedTimestamps.map((timestamp) => {
        const existing = dataMap.get(timestamp)
        if (existing) return existing
        return { timestamp } as TimeSeriesDataPoint
      })
    }
    
    return baseData
  }, [data, aggregatedData, sharedTimestamps])

  /** "Other" only in the filter menu when some bucket has remainder (aggregated − full breakdown). */
  const hasOtherInMenu = useMemo(() => {
    if (!aggregatedData?.length) return false
    const aggregatedMap = new Map(
      aggregatedData.map((p) => [p.timestamp, p.value])
    )
    if (data.length === 0) {
      return aggregatedData.some(
        (p) => typeof p.value === "number" && Number.isFinite(p.value) && p.value > 0.001
      )
    }
    for (const point of data) {
      const ts = point.timestamp as string
      const fullSum = series.reduce((sum, s) => {
        const v = point[s.name]
        return sum + (typeof v === "number" && Number.isFinite(v) ? v : 0)
      }, 0)
      const agg = aggregatedMap.get(ts)
      if (typeof agg !== "number" || !Number.isFinite(agg)) continue
      if (Math.max(0, agg - fullSum) > 0.001) return true
    }
    return false
  }, [aggregatedData, data, series])

  const consumersForMenu = useMemo((): TimeSeriesSeries<string[]>[] => {
    if (!aggregatedData?.length || !hasOtherInMenu) return series
    return [...series, OTHER_SERIES_STUB]
  }, [series, aggregatedData, hasOtherInMenu])

  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(
    () => new Set(consumersForMenu.map((s) => s.name))
  )
  const [showPeaks, setShowPeaks] = useState(defaultShowPeaks)
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null)

  const isMobile = useMemo(() => {
    return window.innerWidth < 768
  }, [])

  useEffect(() => {
    if (consumersForMenu.length > 0) {
      setSelectedAssets(new Set(consumersForMenu.map((s) => s.name)))
    }
  }, [consumersForMenu])

  /**
   * Map selectedAssets to actual phase-data consumer IDs for the PhaseBalanceChart.
   * The phase chart uses a subtractive model: it starts with the total across all
   * consumers and subtracts deselected ones. So we pass the set of consumer IDs
   * that should remain INCLUDED. Known consumers follow the toggle state;
   * "Other" consumers are always included (never subtracted).
   */
  const phaseSelectedConsumers = useMemo((): Set<string> => {
    if (!phaseBalanceData?.length) return selectedAssets
    const knownSeriesNames = new Set(series.map((s) => s.name))
    const result = new Set<string>()
    // Include known consumers that are selected in the filter
    for (const s of series) {
      if (selectedAssets.has(s.name)) {
        result.add(s.name)
      }
    }
    // Always include "Other" consumer IDs (not in known series) so they're never subtracted
    for (const point of phaseBalanceData) {
      if (!knownSeriesNames.has(point.consumerId)) {
        result.add(point.consumerId)
      }
    }
    // Also include _remainder if selected
    if (selectedAssets.has(REMAINDER_KEY)) {
      result.add(REMAINDER_KEY)
    }
    return result
  }, [selectedAssets, series, phaseBalanceData])

  /** Base consumer series only, stable stacking order (matches `series` order). */
  const filteredBaseSeries = useMemo(() => {
    const order = series.map((s) => s.name)
    return [...series]
      .filter((s) => selectedAssets.has(s.name))
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
  }, [series, selectedAssets])

  const filteredData = useMemo(() => {
    const result = breakdownInputData.map((point) => {
      const filtered: TimeSeriesDataPoint = { timestamp: point.timestamp }
      series.forEach((s) => {
        if (selectedAssets.has(s.name)) {
          filtered[s.name] = point[s.name]
          const peakKey = `${s.name}_peak`
          if (peakKey in point) {
            filtered[peakKey] = point[peakKey]
          }
        }
      })
      return filtered
    })
    return result
  }, [breakdownInputData, series, selectedAssets])

  const dataWithRemainder = useMemo(() => {
    if (!aggregatedData || aggregatedData.length === 0) {
      return filteredData
    }

    const aggregatedMap = new Map(
      aggregatedData.map((point) => [point.timestamp, point.value])
    )

    const otherSelected = selectedAssets.has(REMAINDER_KEY)

    return filteredData.map((point) => {
      const assetSum = filteredBaseSeries.reduce((sum, s) => {
        const value = point[s.name]
        return sum + (typeof value === "number" && Number.isFinite(value) ? value : 0)
      }, 0)

      const aggregatedValue = aggregatedMap.get(point.timestamp)
      const remainder =
        typeof aggregatedValue === "number" && Number.isFinite(aggregatedValue)
          ? Math.max(0, aggregatedValue - assetSum)
          : 0

      return {
        ...point,
        _remainder:
          otherSelected && remainder > 0.001 ? remainder : null,
      }
    })
  }, [filteredData, filteredBaseSeries, aggregatedData, selectedAssets])

  const seriesWithRemainder = useMemo(() => {
    const hasRemainder = dataWithRemainder.some(
      (point) => point._remainder !== null && point._remainder !== undefined
    )

    if (!hasRemainder) {
      return filteredBaseSeries
    }

    // Recharts stacks last `<Bar>` on top — keep Other last for a consistent order.
    return [...filteredBaseSeries, OTHER_SERIES_STUB]
  }, [filteredBaseSeries, dataWithRemainder])

  /**
   * Build a per-timestamp map of load values for each series (including _remainder).
   * Used by PhaseBalanceChart to compute the fraction of total load that is selected
   * so phase values can be scaled proportionally when connections are toggled.
   */
  const phaseLoadBarData = useMemo((): Map<string, Map<string, number>> | undefined => {
    if (!phaseDataAvailable || !phaseBalanceData?.length) return undefined
    const map = new Map<string, Map<string, number>>()
    for (const point of dataWithRemainder) {
      const ts = point.timestamp as string
      const row = new Map<string, number>()
      for (const s of seriesWithRemainder) {
        const val = (point as Record<string, unknown>)[s.name]
        if (typeof val === "number" && Number.isFinite(val)) {
          row.set(s.name, val)
        }
      }
      if (row.size > 0) {
        map.set(ts, row)
      }
    }
    return map
  }, [phaseDataAvailable, phaseBalanceData, dataWithRemainder, seriesWithRemainder])

  /** All load series names including _remainder — passed to PhaseBalanceChart for fraction computation */
  const phaseAllLoadSeriesNames = useMemo((): string[] | undefined => {
    if (!phaseDataAvailable) return undefined
    return seriesWithRemainder.map((s) => s.name)
  }, [phaseDataAvailable, seriesWithRemainder])

  const dataWithOverlay = useMemo(() => {
    if (!overlayData) return dataWithRemainder

    // Backfill overlay data to match bar chart timestamps
    const barTimestamps = dataWithRemainder.map((p) => p.timestamp as string)
    const overlayMap = new Map(
      overlayData.data.map((point) => [point.timestamp as string, point])
    )

    // Create backfilled overlay points for every bar timestamp
    const backfilledOverlayData = barTimestamps.map((timestamp) => {
      const existing = overlayMap.get(timestamp)
      if (existing) return existing
      
      // Create null point for missing timestamps
      const nullPoint: TimeSeriesDataPoint = { timestamp }
      overlayData.series.forEach((s) => {
        nullPoint[s.name] = null
      })
      return nullPoint
    })

    // Merge backfilled overlay into bar data
    return dataWithRemainder.map((point, index) => {
      const overlayPoint = backfilledOverlayData[index]
      const merged: Record<string, unknown> = { ...point }
      
      overlayData.series.forEach((overlaySeries) => {
        const overlayKey = `overlay_${overlaySeries.name}`
        merged[overlayKey] = overlayPoint[overlaySeries.name]
      })
      
      return merged as TimeSeriesDataPoint
    })
  }, [dataWithRemainder, overlayData])

  const overlayUsesSameYAxis = overlayData?.useSameYAxis === true

  /**
   * Negatives are detected across the *full* breakdown rather than the current
   * selection, so the reference line doesn't vanish when the consumer that's
   * feeding power back happens to be toggled off.
   */
  const hasNegativeInStack = useMemo(() => {
    const names = series.map((s) => s.name)
    return breakdownInputData.some((point) => {
      const p = point as Record<string, unknown>
      return names.some((n) => {
        const v = p[n]
        return typeof v === "number" && v < 0
      })
    })
  }, [breakdownInputData, series])

  /**
   * System load per bucket, deliberately independent of the consumer filter:
   * the aggregated value from the API when available, otherwise the signed sum
   * of every series. Keeping it unfiltered makes it a fixed reference — toggling
   * consumers shrinks the bars while the line stays put, so you can see how much
   * of the total system load the current selection accounts for.
   */
  const systemLoadByTimestamp = useMemo(() => {
    const map = new Map<string, number>()
    if (aggregatedData?.length) {
      for (const point of aggregatedData) {
        const v = point.value
        if (typeof v === "number" && Number.isFinite(v)) {
          map.set(point.timestamp as string, v)
        }
      }
      return map
    }
    const names = series.map((s) => s.name)
    for (const point of breakdownInputData) {
      const p = point as Record<string, unknown>
      let net = 0
      let hasValue = false
      for (const n of names) {
        const v = p[n]
        if (typeof v === "number" && Number.isFinite(v)) {
          net += v
          hasValue = true
        }
      }
      if (hasValue) map.set(point.timestamp as string, net)
    }
    return map
  }, [aggregatedData, breakdownInputData, series])

  const dataWithNetLoad = useMemo(() => {
    if (!hasNegativeInStack) return dataWithOverlay
    return dataWithOverlay.map((point) => {
      const v = systemLoadByTimestamp.get(point.timestamp as string)
      return {
        ...point,
        [NET_LOAD_KEY]: typeof v === "number" ? v : null,
      } as TimeSeriesDataPoint
    })
  }, [dataWithOverlay, systemLoadByTimestamp, hasNegativeInStack])

  const dataForChart = useMemo(() => appendTimeMs(dataWithNetLoad), [dataWithNetLoad])

  const stackedTimeDomain = useMemo(
    () => timescaleTimeDomain(xAxisTicksMs, chartAxisStartDate, chartAxisEndDate),
    [chartAxisStartDate, chartAxisEndDate, xAxisTicksMs]
  )

  const stackedTimeMinTickGap = useMemo(
    () => timescaleMinTickGap(xAxisTickInterval, dataForChart),
    [xAxisTickInterval, dataForChart]
  )

  const formatStackedTimeTick = useMemo(
    () => timescaleTickFormatter(timescaleSpanMs(chartAxisStartDate, chartAxisEndDate)),
    [chartAxisStartDate, chartAxisEndDate]
  )

  const overlaySeries = useMemo(() => {
    if (!overlayData) return []
    return overlayData.series.map((s) => ({
      ...s,
      name: `overlay_${s.name}`,
      color: OVERLAY_LINE_COLOR,
      strokeDasharray: undefined,
      strokeWidth: 1,
    }))
  }, [overlayData])

  const allSeries = useMemo(() => {
    return [...seriesWithRemainder, ...overlaySeries]
  }, [seriesWithRemainder, overlaySeries])

  const seriesLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    seriesWithRemainder.forEach((s) => {
      map.set(s.name, s.label || s.name)
    })
    overlaySeries.forEach((s) => {
      map.set(s.name, overlayData?.yAxisLabel || "Site load")
    })
    map.set(NET_LOAD_KEY, NET_LOAD_LABEL)
    return map
  }, [seriesWithRemainder, overlaySeries, overlayData])

  /**
   * Every Load series (ticked or not) plus "Other" when the menu has it. The label
   * map above only holds ticked series, so the per-asset phase chart needs this one
   * to tell an unticked asset from phase data that has no Load series at all.
   */
  const allSeriesLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    consumersForMenu.forEach((s) => map.set(s.name, s.label || s.name))
    return map
  }, [consumersForMenu])

  const seriesColorMap = useMemo(() => {
    const map = new Map<string, string>()
    seriesWithRemainder.forEach((s) => {
      map.set(s.name, s.color)
    })
    return map
  }, [seriesWithRemainder])

  /** Compute Y-axis domain that accommodates peak values above the stacked bars and negative values below */
  const yAxisDomain = useMemo((): [number | string, number | string] | undefined => {
    const allBarNames = seriesWithRemainder.map((s) => s.name)
    let maxPeakTotal = 0
    let minNegativeTotal = 0
    for (const point of dataWithOverlay) {
      let totalPeak = 0
      let totalNegative = 0
      let hasPeak = false
      const p = point as Record<string, unknown>
      for (const name of allBarNames) {
        const peakKey = `${name}_peak`
        const peakVal = p[peakKey]
        if (typeof peakVal === "number" && Number.isFinite(peakVal)) {
          totalPeak += peakVal
          hasPeak = true
        } else {
          const val = p[name]
          if (typeof val === "number" && Number.isFinite(val)) {
            totalPeak += val
            if (val < 0) totalNegative += val
          }
        }
      }
      if (hasPeak && showPeaks && totalPeak > maxPeakTotal) {
        maxPeakTotal = totalPeak
      }
      if (totalNegative < minNegativeTotal) {
        minNegativeTotal = totalNegative
      }
    }
    const hasNegative = minNegativeTotal < 0
    const hasPeakDomain = showPeaks && maxPeakTotal > 0
    if (!hasNegative && !hasPeakDomain) return undefined
    const yMin = hasNegative ? Math.min(minNegativeTotal * 1.1, -0.05) : 0
    const yMax = hasPeakDomain ? niceYAxisMax(maxPeakTotal * 1.05) : (dataMax: number) => niceYAxisMax(dataMax)
    return [yMin, yMax as number | string]
  }, [showPeaks, dataWithOverlay, seriesWithRemainder])

  /** True when any breakdown series has a negative value — drives y-axis lower bound and bottom margin */
  const hasNegativeValues = useMemo(() => {
    const allBarNames = seriesWithRemainder.map((s) => s.name)
    for (const point of dataWithOverlay) {
      const p = point as Record<string, unknown>
      for (const name of allBarNames) {
        const val = p[name]
        if (typeof val === "number" && val < 0) return true
      }
    }
    return false
  }, [dataWithOverlay, seriesWithRemainder])

  // Dynamic series keys (assets, _remainder, overlays) — wider than useChart's inferred keys
  const chart = useChart({
    data: dataForChart,
    series: allSeries,
  } as unknown as Parameters<typeof useChart>[0])

  const handleAssetToggle = (assetName: string, isSelected: boolean) => {
    setSelectedAssets((prev) => {
      const newSet = new Set(prev)
      if (isSelected) {
        newSet.add(assetName)
      } else {
        if (prev.size > 1) {
          newSet.delete(assetName)
        }
      }
      return newSet
    })
  }

  const handleSelectAllConsumers = () => {
    const allSelected = selectedAssets.size === consumersForMenu.length
    if (allSelected) {
      const first = consumersForMenu[0]?.name
      if (first) {
        setSelectedAssets(new Set([first]))
      }
    } else {
      setSelectedAssets(new Set(consumersForMenu.map((s) => s.name)))
    }
  }

  const formatYAxisTick = (value: number) => {
    if (value === 0) return "0"
    if (Math.abs(value) >= 1_000_000) {
      return value.toLocaleString(undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
      })
    }
    if (Number.isInteger(value)) {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    }
    if (Math.abs(value) < 1) {
      return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")
    }
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  const unit = useMemo(() => {
    if (graph.source?.desiredUnit) {
      return graph.source.desiredUnit
    }
    const yAxisMatch = graph.yAxis?.match(/\(([^)]+)\)/)
    return yAxisMatch?.[1] || undefined
  }, [graph])

  const stackedTotalLabel = useMemo(() => {
    const aggregatedLabel = (graph as { aggregatedValueLabel?: string }).aggregatedValueLabel
    if (aggregatedLabel) return aggregatedLabel
    if (graph.yAxis) {
      const withoutUnit = graph.yAxis.replace(/\s*\([^)]*\)\s*$/, "").trim()
      if (withoutUnit) return withoutUnit
    }
    return "Total"
  }, [graph])

  // Both the Load graph (formerly "Consumers") and the generation graph use
  // "assets" wording: a load source such as an inverter or DC-AC solar is an
  // asset, not a consumer, so the menu should not call it a consumer.
  const menuAllSelectedLabel = "All Assets"
  const menuEntityPlural = "assets"

  return (
    <Box position="relative">
      <Flex mb={4} alignItems="center" justifyContent="space-between" gap={3} flexWrap="wrap">
        <Flex alignItems="center" minW={0}>
          <Heading fontFamily="montserrat" size="sm">
            {graph.title}
          </Heading>
        </Flex>

        <Flex alignItems="center" gap={3}>
          {showPeakToggle && (
            <Flex alignItems="center" gap={2}>
              <Text fontSize="sm" color="fg.muted">
                Show peaks
              </Text>
              <Switch
                checked={showPeaks}
                onCheckedChange={(details) => setShowPeaks(details.checked)}
              />
            </Flex>
          )}

          {consumersForMenu.length > 0 && (
            <Menu.Root closeOnSelect={false}>
              <Menu.Trigger asChild>
                <Box
                  as="button"
                  padding={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="border"
                  backgroundColor="white"
                  cursor="pointer"
                  width={{ base: "200px", md: "250px" }}
                  textAlign="left"
                  fontSize="sm"
                  _hover={{ borderColor: "blue.300" }}
                >
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text truncate>
                      {selectedAssets.size === consumersForMenu.length
                        ? `${menuAllSelectedLabel} (${selectedAssets.size})`
                        : `${selectedAssets.size}/${consumersForMenu.length} ${menuEntityPlural}`}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      ▼
                    </Text>
                  </Flex>
                </Box>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content width="400px" maxH="500px" overflowY="auto">
                  <Box borderBottomWidth="2px" borderColor="gray.200" pb={2} mb={3} mx={2}>
                    <Flex alignItems="center" justifyContent="space-between" gap={4} fontSize="sm">
                      <Link variant="primary" onClick={handleSelectAllConsumers} cursor="pointer">
                        Select all
                      </Link>
                    </Flex>
                  </Box>

                  {consumersForMenu.map((asset) => {
                    const isAssetSelected = selectedAssets.has(asset.name)
                    return (
                      <Box key={asset.name} mx={2} mb={1}>
                        <Menu.Item
                          value={asset.name}
                          onSelect={() => handleAssetToggle(asset.name, !isAssetSelected)}
                          cursor="pointer"
                          _hover={{ backgroundColor: "gray.50" }}
                          borderRadius="sm"
                        >
                          <Flex alignItems="center" gap={2}>
                            <input
                              type="checkbox"
                              checked={isAssetSelected}
                              disabled={selectedAssets.size === 1 && isAssetSelected}
                              onChange={() => { }}
                            />
                            <Badge
                              backgroundColor={asset.color}
                              color="white"
                              fontSize="xs"
                              minW="12px"
                              textAlign="center"
                              opacity={isAssetSelected ? 1 : 0.5}
                            >
                              ●
                            </Badge>
                            <Text fontSize="sm" color={isAssetSelected ? "black" : "gray.500"}>
                              {asset.label}
                            </Text>
                          </Flex>
                        </Menu.Item>
                      </Box>
                    )
                  })}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          )}

          {showBackToggle && (
            <Button size="sm" variant="hydrologiq_secondary" onClick={onBack}>
              View system summary
            </Button>
          )}
        </Flex>
      </Flex>
      <Chart.Root h={chartHeight} maxH={chartHeight} chart={chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            key={`chart-${showPeaks}`}
            data={dataForChart}
            syncId={phaseDataAvailable ? "load-phase-sync" : undefined}
            // "sign" stacks positive segments up from 0 and negative segments
            // down from 0 independently, instead of accumulating them onto
            // whatever the running stack total is. Without this, a negative
            // segment (e.g. Inverter/Charger feeding back) drags the whole
            // stack below the axis instead of resting the positive portion on 0.
            stackOffset="sign"
            margin={{
              top: 5,
              right: timescaleChartMarginRight(Boolean(overlayData && !overlayUsesSameYAxis)),
              left: 20,
              bottom: phaseDataAvailable ? 10 : hasNegativeValues ? 40 : 10,
            }}
            barCategoryGap="6%"
          >
            <CartesianGrid stroke={chart.color("border")} strokeDasharray="3 3" vertical={false} />
            {hasNegativeValues && (
              <ReferenceLine yAxisId="left" y={0} stroke="#999" strokeWidth={1} />
            )}
            {highlightRanges?.map((range, index) => {
              const { x1, x2 } = chartHighlightToMsRange(range)
              return (
              <ReferenceArea
                key={`highlight-${index}`}
                yAxisId="left"
                x1={x1}
                x2={x2}
                fill={range.color || "rgba(255, 165, 0, 0.2)"}
                fillOpacity={0.3}
                stroke={range.color || "rgba(255, 165, 0, 0.6)"}
                strokeWidth={4}
                label={
                  range.label
                    ? {
                        value: range.label,
                        position: "insideTop",
                        fill: "#666",
                        fontSize: 11,
                      }
                    : undefined
                }
              />
            )
            })}
            <XAxis
              axisLine={false}
              dataKey="timeMs"
              type="number"
              scale="time"
              domain={stackedTimeDomain ?? ["dataMin", "dataMax"]}
              stroke={chart.color("border")}
              fontSize={12}
              tickLine={false}
              tickFormatter={formatStackedTimeTick}
              ticks={xAxisTicksMs}
              minTickGap={xAxisTicksMs?.length ? undefined : stackedTimeMinTickGap}
            />
            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              stroke={chart.color("border")}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              allowDecimals={false}
              tickCount={6}
              domain={yAxisDomain ?? [(dataMin: number) => dataMin < 0 ? dataMin * 1.1 : 0, (dataMax: number) => niceYAxisMax(dataMax)]}
              label={
                graph?.yAxis
                  ? {
                    value: graph.yAxis,
                    angle: -90,
                    position: "insideLeft",
                  }
                  : undefined
              }
            />
            {overlayData && !overlayUsesSameYAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                width={TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                stroke={chart.color("border")}
                fontSize={12}
                tickFormatter={formatYAxisTick}
                allowDecimals={false}
                tickCount={6}
                domain={[0, (dataMax: number) => niceYAxisMax(dataMax)]}
                label={{
                  value: overlayData.yAxisLabel,
                  angle: 90,
                  position: "insideRight",
                }}
              />
            )}
            <Tooltip
              animationDuration={0}
              wrapperStyle={{ zIndex: 1000 }}
              content={
                <CustomBarTooltip
                  series={allSeries}
                  aggregatedData={aggregatedData}
                  unit={unit}
                  data={dataForChart}
                  totalLabel={stackedTotalLabel}
                  showPeaks={showPeaks}
                />
              }
            />
            {!isMobile && (
              <Legend
                verticalAlign="top"
                align="center"
                content={(props) => (
                  <StackedBarLegend
                    {...props}
                    onSeriesHover={setHoveredSeries}
                    hoveredSeries={hoveredSeries}
                    seriesLabelMap={seriesLabelMap}
                  />
                )}
              />
            )}
            {chart.series.map((item) => {
              const itemName = String(item.name ?? "")
              const isOverlay = itemName.startsWith("overlay_")
              
              if (isOverlay) {
                return (
                  <Line
                    key={itemName}
                    yAxisId={overlayUsesSameYAxis ? "left" : "right"}
                    isAnimationActive={false}
                    dataKey={chart.key(itemName as never)}
                    stroke={chart.color(item.color)}
                    strokeWidth={1}
                    strokeOpacity={hoveredSeries && hoveredSeries !== itemName ? 0.5 : 1}
                    dot={false}
                    connectNulls={true}
                    type="monotone"
                  />
                )
              }

              // Determine if this is the topmost bar in the stack
              // The last non-overlay series in chart.series is the topmost
              const barSeriesItems = chart.series.filter((s) => !String(s.name ?? "").startsWith("overlay_"))
              const isTopOfStack = barSeriesItems.length > 0 && String(barSeriesItems[barSeriesItems.length - 1].name ?? "") === itemName
              const allBarSeriesNames = seriesWithRemainder.map((s) => s.name)
              
              return (
                <Bar
                  key={itemName}
                  yAxisId="left"
                  isAnimationActive={false}
                  dataKey={chart.key(itemName as never)}
                  fill={chart.color(item.color)}
                  fillOpacity={hoveredSeries && hoveredSeries !== itemName ? 0.5 : 1}
                  stackId="breakdown"
                  maxBarSize={80}
                  shape={(shapeProps: any) => (
                    <CustomBarShape
                      {...shapeProps}
                      data={dataForChart}
                      showPeaks={showPeaks}
                      isTopOfStack={isTopOfStack}
                      allBarSeriesNames={allBarSeriesNames}
                    />
                  )}
                />
              )
            })}
            {hasNegativeInStack && (
              <Line
                yAxisId="left"
                isAnimationActive={false}
                dataKey={NET_LOAD_KEY}
                name={NET_LOAD_KEY}
                stroke={OVERLAY_LINE_COLOR}
                strokeWidth={2}
                strokeOpacity={hoveredSeries ? 0.4 : 0.9}
                dot={false}
                connectNulls
                type="linear"
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </Chart.Root>
      {phaseDataAvailable && phaseBalanceData && (
        <PhaseBalanceChart
          phaseData={phaseBalanceData}
          selectedConsumers={phaseSelectedConsumers}
          timeDomain={stackedTimeDomain ?? [
            (dataForChart[0] as { timeMs: number })?.timeMs ?? 0,
            (dataForChart[dataForChart.length - 1] as { timeMs: number })?.timeMs ?? 0,
          ]}
          xAxisTicksMs={xAxisTicksMs}
          scaleToKw={unit === "kW"}
          onSeriesHover={setHoveredSeries}
          hoveredSeries={hoveredSeries}
          sharedTimestamps={sharedTimestamps}
          syncId="load-phase-sync"
          consumerLabelMap={phasePerAsset ? allSeriesLabelMap : seriesLabelMap}
          consumerColorMap={seriesColorMap}
          loadBarData={phaseLoadBarData}
          allLoadSeriesNames={phaseAllLoadSeriesNames}
          mode={phaseChartMode}
          chartHeight={chartHeight}
          showPeaks={showPeaks}
          highlightRanges={highlightRanges}
          highlightPhase={highlightPhase}
          perAsset={phasePerAsset}
        />
      )}
    </Box>
  )
}

export default StackedBarBreakdownChart